import { z } from 'zod'
import { dateOrIsoString } from '~/prix/types/date'
import { lineString, multiLineString, point } from '~/prix/types/presets/geo'
import sql, { combineSQL } from '~/prix/utils/sql'
import { complementaryRoadSourceFilterExpression } from '../complementaryRoad/complementaryRoadSql'

const emptyTextArray = sql`CAST(ARRAY[] AS VARCHAR[])`
const emptyTimestamp = sql`CAST('1970-01-01 00:00:00+00' AS TIMESTAMPTZ)`

export const roadSource = z.enum(['officialStreet', 'complementaryRoad'])

export const roadPath = z.union([lineString(), multiLineString()])

export const roadObject = z.object({
  key: z.string(),
  source: roadSource,
  remoteId: z.string(),
  officialStreetId: z.number().int().nullish(),
  complementaryRoadId: z.string().uuid().nullish(),
  name: z.string().nullish(),
  alternativeNames: z.array(z.string()).optional().default([]),
  type: z.literal('street'),
  municipalityId: z.number().int(),
  point: point().nullish(),
  path: roadPath.nullish(),
  createdAt: dateOrIsoString().nullish(),
  updatedAt: dateOrIsoString().nullish(),
  synchronizedAt: dateOrIsoString().nullish(),
  deletedAt: dateOrIsoString().nullish(),
})

export type RoadType = z.infer<typeof roadObject>

export const roadPropertyMap = {
  key: sql`road.key`,
  source: sql`road.source`,
  remoteId: sql`road.remote_id`,
  officialStreetId: sql`road.official_street_id`,
  complementaryRoadId: sql`road.complementary_road_id`,
  name: sql`road.name`,
  alternativeNames: sql`road.alternative_names`,
  type: sql`road.type`,
  municipalityId: sql`road.municipality_id`,
  point: sql`road.point`,
  path: sql`road.path`,
  createdAt: sql`road.created_at`,
  updatedAt: sql`road.updated_at`,
  synchronizedAt: sql`road.synchronized_at`,
  deletedAt: sql`road.deleted_at`,
}

export function normalizeRoadSearchText(value: string) {
  return sql`
    TRANSLATE(
      LOWER(${value}),
      'áàãâäéèêẽëíìîĩïóòõôöúùûũüç',
      'aaaaaeeeeeiiiiiooooouuuuuc'
    )
  `
}

export function officialStreetSourceFilterExpression() {
  return sql`
    TRANSLATE(
      LOWER(
        CONCAT_WS(
          ' ',
          official_street.name,
          array_to_string(official_street.alternative_names, ' '),
          array_to_string(official_street.natural_alternative_names, ' '),
          official_street.ref,
          array_to_string(official_street.old_refs, ' ')
        )
      ),
      'áàãâäéèêẽëíìîĩïóòõôöúùûũüç',
      'aaaaaeeeeeiiiiiooooouuuuuc'
    )
  `
}

export function officialStreetSynchronizedAtExpression() {
  return sql`
    GREATEST(
      COALESCE(official_street.updated_at, official_street.created_at, ${emptyTimestamp}),
      COALESCE(official_street.deleted_at, official_street.updated_at, official_street.created_at, ${emptyTimestamp})
    )
  `
}

export function buildRoadUnionStatement({
  municipalityId,
  complementaryRoadClientIds,
}: {
  municipalityId: number
  complementaryRoadClientIds: number[]
}) {
  const complementaryRoadClientFilter =
    complementaryRoadClientIds.length > 0
      ? sql`complementary_road.client_id IN (${combineSQL(complementaryRoadClientIds, ',')})`
      : sql`FALSE`

  return sql`
    SELECT
      CONCAT('street:', official_street.id) AS key,
      ${'officialStreet'} AS source,
      CAST(official_street.id AS TEXT) AS remote_id,
      official_street.id AS official_street_id,
      CAST(NULL AS UUID) AS complementary_road_id,
      official_street.name,
      COALESCE(official_street.alternative_names, ${emptyTextArray}) AS alternative_names,
      ${'street'} AS type,
      street_municipality_intersection.municipality_id,
      CAST(ST_AsGeoJSON(official_street.point) AS JSON) AS point,
      CAST(ST_AsGeoJSON(official_street.path) AS JSON) AS path,
      official_street.created_at,
      official_street.updated_at,
      ${officialStreetSynchronizedAtExpression()} AS synchronized_at,
      official_street.deleted_at,
      ${officialStreetSourceFilterExpression()} AS source_filter
    FROM geo.street AS official_street
    INNER JOIN geo.street_municipality_intersection AS street_municipality_intersection
      ON street_municipality_intersection.street_id = official_street.id
    WHERE street_municipality_intersection.municipality_id = ${municipalityId}

    UNION ALL

    SELECT
      CONCAT('complementary-road:', complementary_road.id) AS key,
      ${'complementaryRoad'} AS source,
      CAST(complementary_road.id AS TEXT) AS remote_id,
      CAST(NULL AS INT) AS official_street_id,
      complementary_road.id AS complementary_road_id,
      complementary_road.name,
      COALESCE(complementary_road.alternative_names, ${emptyTextArray}) AS alternative_names,
      ${'street'} AS type,
      complementary_road.municipality_id,
      CAST(ST_AsGeoJSON(complementary_road.point) AS JSON) AS point,
      CAST(ST_AsGeoJSON(ST_LineMerge(complementary_road.path)) AS JSON) AS path,
      complementary_road.created_at,
      complementary_road.updated_at,
      complementary_road.synchronized_at,
      complementary_road.deleted_at,
      COALESCE(
        complementary_road.source_filter,
        ${complementaryRoadSourceFilterExpression('complementary_road')}
      ) AS source_filter
    FROM geo.complementary_road AS complementary_road
    WHERE complementary_road.municipality_id = ${municipalityId}
      AND ${complementaryRoadClientFilter}
  `
}

export function buildRoadRoutingSourceStatement({
  municipalityId,
  complementaryRoadClientIds,
}: {
  municipalityId: number
  complementaryRoadClientIds: number[]
}) {
  const complementaryRoadClientFilter =
    complementaryRoadClientIds.length > 0
      ? sql`complementary_road.client_id IN (${combineSQL(complementaryRoadClientIds, ',')})`
      : sql`FALSE`

  return sql`
    SELECT
      CONCAT('street:', official_street.id) AS source_road_key,
      ${'officialStreet'} AS source,
      official_street.name,
      street_municipality_intersection.municipality_id,
      official_street.path AS path_geometry,
      CAST(NULL AS TEXT) AS surface,
      CAST(NULL AS TEXT) AS road_class,
      ${officialStreetSynchronizedAtExpression()} AS synchronized_at
    FROM geo.street AS official_street
    INNER JOIN geo.street_municipality_intersection AS street_municipality_intersection
      ON street_municipality_intersection.street_id = official_street.id
    WHERE street_municipality_intersection.municipality_id = ${municipalityId}
      AND official_street.deleted_at IS NULL
      AND official_street.path IS NOT NULL

    UNION ALL

    SELECT
      CONCAT('complementary-road:', complementary_road.id) AS source_road_key,
      ${'complementaryRoad'} AS source,
      complementary_road.name,
      complementary_road.municipality_id,
      ST_LineMerge(complementary_road.path) AS path_geometry,
      CAST(NULL AS TEXT) AS surface,
      CAST(NULL AS TEXT) AS road_class,
      complementary_road.synchronized_at
    FROM geo.complementary_road AS complementary_road
    WHERE complementary_road.municipality_id = ${municipalityId}
      AND ${complementaryRoadClientFilter}
      AND complementary_road.deleted_at IS NULL
      AND complementary_road.path IS NOT NULL
  `
}

export const roadSelectColumns = sql`
  road.key,
  road.source,
  road.remote_id,
  road.official_street_id,
  road.complementary_road_id,
  road.name,
  road.alternative_names,
  road.type,
  road.municipality_id,
  road.point,
  road.path,
  road.created_at,
  road.updated_at,
  road.synchronized_at,
  road.deleted_at
`
